import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import { observer } from "mobx-react";
import { MantineProvider, v8CssVariablesResolver } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { AudioProvider } from "./context/audio";
import { theme } from "./theme/mantine-theme";
import { colorSchemeManager } from "./theme/color-scheme";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "./styles/fonts.css";
import "./styles/global.css";
import { Header } from "./components/Header";
import { Content } from "./components/layout/Content";
import { PageLoading } from "./components/layout/PageLoading";
import { SEO } from "./components/SEO";
import { AppRoutes } from "./components/routing/AppRoutes";
import { ScrollToTop } from "./components/routing/ScrollToTop";
import { ShellHeadFallback } from "./components/routing/ShellHeadFallback";
import { ThemeMeta } from "./components/theme/ThemeMeta";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Player } from "./components/player/Player";
import { AdminView } from "./components/LazyImports";
import { isAdminPath } from "./core/adminSections";
import { useStores } from "./hooks/useStores";

// Closed until used, so they are not part of what the first page needs; they load in the background
const SpotlightSearch = lazy(() =>
    import("./components/search/SpotlightSearch").then(({ SpotlightSearch }) => ({ default: SpotlightSearch }))
);
const SignInModal = lazy(() =>
    import("./components/auth/SignInModal").then(({ SignInModal }) => ({ default: SignInModal }))
);
const ProfileSetupModal = lazy(() =>
    import("./components/auth/ProfileSetupModal").then(({ ProfileSetupModal }) => ({ default: ProfileSetupModal }))
);

const PlaylistModal = lazy(() =>
    import("./components/playlist/PlaylistModal").then(({ PlaylistModal }) => ({ default: PlaylistModal }))
);

const OptionalPlaylistModal = observer(() => {
    const { playlistStore } = useStores();

    if (!playlistStore.isOpen) {
        return null;
    }

    return (
        <Suspense fallback={null}>
            <PlaylistModal />
        </Suspense>
    );
});

// Everything that belongs to the public site: audio, search, sign-in, the player and the site head
const SiteRoot = () => {
    const { categoryStore } = useStores();
    // Categories are needed on most pages (autolinking), so load them as the site mounts, not on the admin area
    useEffect(() => {
        void categoryStore.preload();
    }, [categoryStore]);

    return (
        <AudioProvider>
            <ShellHeadFallback />
            <SEO />
            <ErrorBoundary>
                <Suspense fallback={null}>
                    <SpotlightSearch />
                </Suspense>
            </ErrorBoundary>
            <Content>
                <ErrorBoundary>
                    <Header />
                </ErrorBoundary>
                <ErrorBoundary>
                    <AppRoutes />
                </ErrorBoundary>
            </Content>
            <ErrorBoundary>
                <OptionalPlaylistModal />
            </ErrorBoundary>
            <ErrorBoundary>
                <Suspense fallback={null}>
                    <SignInModal />
                </Suspense>
            </ErrorBoundary>
            <ErrorBoundary>
                <Suspense fallback={null}>
                    <ProfileSetupModal />
                </Suspense>
            </ErrorBoundary>
            <ErrorBoundary>
                <Player />
            </ErrorBoundary>
        </AudioProvider>
    );
};

// The admin area mounts none of the site chrome: no audio, player, search or sign-in modals
const AdminRoot = () => (
    <ErrorBoundary>
        <Suspense fallback={<PageLoading />}>
            <AdminView />
        </Suspense>
    </ErrorBoundary>
);

const AppRouteTree = () => {
    const { pathname } = useLocation();
    return isAdminPath(pathname) ? <AdminRoot /> : <SiteRoot />;
};

export const App = () => {
    const { authStore } = useStores();
    useEffect(() => {
        authStore.restore();
    }, [authStore]);

    return (
        <MantineProvider
            colorSchemeManager={colorSchemeManager}
            cssVariablesResolver={v8CssVariablesResolver}
            defaultColorScheme="auto"
            theme={theme}
            withGlobalClasses>
            <ThemeMeta />
            <Notifications pauseResetOnHover="notification" zIndex={10004} />
            <BrowserRouter useTransitions>
                <ErrorBoundary>
                    <ScrollToTop />
                    <AppRouteTree />
                </ErrorBoundary>
            </BrowserRouter>
        </MantineProvider>
    );
};
